import axios from 'axios';
import { withRetry, handleAPIError } from './errorHandler';

// Create axios instance with base configuration
// Since we have proxy: "http://localhost:3000" in package.json,
// we use empty baseURL and all endpoints should start with /api/
const apiClient = axios.create({
  baseURL: 'https://premierchoicecrm.onrender.com', // Empty because we use proxy
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Validate token format before using it
      if (token.split('.').length === 3) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('Invalid token format detected, clearing auth data');
        localStorage.removeItem('token');
        localStorage.removeItem('admin');
        localStorage.removeItem('refresh_token');
        // Redirect to login if not already there
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('admin');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Enhanced API methods with retry logic
const createEnhancedMethod = (method) => {
  return async (url, dataOrConfig, config) => {
    switch (method) {
      case 'get':
      case 'delete':
        return await apiClient[method](url, dataOrConfig);
      case 'post':
      case 'put':
        return await apiClient[method](url, dataOrConfig, config);
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  };
};

// Create enhanced API client with retry logic
const enhancedApiClient = {
  get: createEnhancedMethod('get'),
  post: createEnhancedMethod('post'),
  put: createEnhancedMethod('put'),
  delete: createEnhancedMethod('delete'),
  // Keep original methods for special cases
  original: apiClient
};

// API endpoints - all start with /api/ to work with proxy
export const endpoints = {
  // Authentication
  auth: {
    login: '/api/auth/login',
    forgotPassword: '/api/auth/forgot-password',
    resetPassword: '/api/auth/reset-password'
  },
  
  // Dashboard
  dashboard: {
    users: '/api/dashboard/users',
    certificates: '/api/dashboard/certificates',
    reminders: '/api/dashboard/reminders',
    auditLogs: '/api/dashboard/audit-logs',
    summary: '/api/dashboard/summary',
    expiring: '/api/dashboard/expiring',
    renewalTrend: '/api/dashboard/renewal-trend',
    reminderStats: '/api/dashboard/reminder-stats',
    certificateTypes: '/api/dashboard/certificate-types',
    export: '/api/dashboard/export'
  },
  
  // Users
  users: {
    list: '/api/users',
    byId: (id) => `/api/users/${id}`,
    tags: (id) => `/api/users/${id}/tags`,
  },
  
  // Certificates
  certificates: {
    list: '/api/certificates',
    byId: (id) => `/api/certificates/${id}`,
    delete: (id) => `/api/certificates/${id}`,
    download: (id) => `/api/certificates/${id}/download`,
    create: '/api/certificates',
    update: (id) => `/api/certificates/${id}`,
    byUser: (userId) => `/api/certificates/user/${userId}`,
    tags: (id) => `/api/certificates/${id}/tags`,
  },
  
  // Tags
  tags: {
    list: '/api/tags',
    byId: (id) => `/api/tags/${id}`,
    create: '/api/tags',
    update: (id) => `/api/tags/${id}`,
    delete: (id) => `/api/tags/${id}`
  },
  
  // Sub Admin
  subAdmin: {
    create: '/api/sub-admin',
    list: '/api/sub-admin',
    byId: (id) => `/api/sub-admin/${id}`,
    status: (id) => `/api/sub-admin/${id}/status`,
    invite: (id) => `/api/sub-admin/${id}/invite`,
    login: '/api/sub-admin/login',
    roles: '/api/sub-admin/roles',
    roleById: (id) => `/api/sub-admin/roles/${id}`
  },
  
  // Reminders
  reminders: {
    send: (id) => `/api/reminders/send/${id}`,
    resend: (id) => `/api/reminders/resend/${id}`
  },
  
  // Templates
  templates: {
    email: '/api/templates/email',
    emailPreview: (id) => `/api/templates/email/${id}/preview`,
    sms: '/api/templates/sms',
    smsPreview: (id) => `/api/templates/sms/${id}/preview`
  },
  
  // Settings
  settings: {
    get: '/api/settings',
    update: '/api/settings',
    uploadLogo: '/api/settings/upload-logo'
  }
};

export default enhancedApiClient;